import { ApiError, getApiBaseUrl, requestJson } from "./api";
import type { OrderDetailDto } from "./ordersApi";
import { toOrderDetail } from "./ordersApi";
import type { OrderDetail } from "./types";

function buildUrl(path: string) {
  return new URL(path, `${getApiBaseUrl()}/`).toString();
}

export type AlterationChangeType =
  | "QUANTITY_CHANGE"
  | "ADD"
  | "CANCEL"
  | "NO_CHANGE"
  | "UNRESOLVED";

export interface AlterationProductSuggestion {
  productId: string;
  name: string;
}

export interface AlterationLine {
  lineNumber: number;
  changeType: AlterationChangeType;
  externalCode: string;
  productNameFromPdf: string;
  unitFromPdf: string | null;
  originalQuantityFromPdf: number | null;
  newQuantityFromPdf: number | null;
  orderItemId: string | null;
  orderItemProductName: string | null;
  orderItemCurrentQuantity: number | null;
  orderItemAlreadyCancelled: boolean;
  mappedProductId: string | null;
  mappedProductName: string | null;
  mappedUnitId: string | null;
  mappedUnitName: string | null;
  resolvedUnitPrice: number | null;
  suggestions: AlterationProductSuggestion[];
}

export interface OrderAlterationPreview {
  orderId: string;
  authorizationNumber: string | null;
  deliveryDate: string | null;
  lines: AlterationLine[];
  warnings: string[];
  errors: string[];
}

export type AlterationActionType = "QUANTITY_CHANGE" | "ADD" | "CANCEL";

export interface AlterationLineAction {
  apply: boolean;
  type: AlterationActionType;
  orderItemId: string | null;
  externalCode: string | null;
  catalogProductId: string | null;
  catalogUnitId: string | null;
  newQuantity: number | null;
  rememberMapping: boolean;
}

export interface OrderAlterationApplyRequest {
  actions: AlterationLineAction[];
}

export async function previewOrderAlteration(
  orderId: string,
  file: File,
): Promise<OrderAlterationPreview> {
  const form = new FormData();
  form.append("file", file);

  const response = await fetch(
    buildUrl(`/api/v1/admin/orders/${orderId}/alteration-pdf/preview`),
    {
      method: "POST",
      credentials: "include",
      body: form,
    },
  );

  if (!response.ok) {
    let details: unknown = null;
    try {
      details = await response.json();
    } catch {
      details = await response.text().catch(() => null);
    }
    const message =
      details && typeof details === "object" && "message" in details
        ? String((details as { message: unknown }).message)
        : "Não foi possível ler o XML de alteração.";
    throw new ApiError(message, response.status, details);
  }

  return response.json() as Promise<OrderAlterationPreview>;
}

export async function applyOrderAlteration(
  orderId: string,
  request: OrderAlterationApplyRequest,
): Promise<OrderDetail> {
  const dto = await requestJson<OrderDetailDto>(
    `/api/v1/admin/orders/${orderId}/alteration-pdf/apply`,
    { method: "POST", body: request },
  );
  return toOrderDetail(dto);
}
